import { router } from '@inertiajs/react'
import { useEffect } from 'react'

import { startTracking, trackEvent, trackPageView } from '@/lib/track'

/**
 * Mounted once inside Layout. Owns all *ambient* tracking:
 *
 *   - `cta_click` for primary calls to action,
 *   - `form_field` the first time each form field is filled in — fired on
 *     blur, so it records abandoned checkouts too, not just submitted ones.
 *
 * Page views, dwell time and engagement come from the shared trackhub tracker
 * that `startTracking` loads; it measures active and engaged time, scroll
 * depth and input, and beats every 5 seconds while the tab is visible.
 *
 * Explicit commerce actions (add to cart, buy now, place order) are tracked at
 * their call sites where the product/order context is available.
 */
export default function Tracker() {
  // Load the shared tracker; it opens the first page view itself. The Inertia
  // hook is belt-and-braces — the tracker also watches the history API.
  useEffect(() => {
    startTracking()
    const stopListening = router.on('navigate', () => trackPageView())

    return () => stopListening()
  }, [])

  // Primary CTA clicks.
  useEffect(() => {
    const onClick = (event: MouseEvent) => {
      const target = event.target as HTMLElement | null
      const el = target?.closest('a, button') as HTMLElement | null
      if (!el) return

      // Commerce buttons report themselves with full context — don't
      // double-count them here.
      if (el.closest('[data-track-skip]')) return

      const cta = el.closest('[data-track-cta]') as HTMLElement | null
      if (cta) {
        trackEvent('cta_click', {
          label: namedValue(cta.dataset.trackCta) ?? elementLabel(el),
          stage: 'clicked_cta',
        })
        return
      }

      const href = el.getAttribute('href')
      if (href && /^\/products\/\d+/.test(href)) {
        trackEvent('cta_click', { label: productLabel(el) ?? href, stage: 'clicked_cta' })
      }
    }

    document.addEventListener('click', onClick, true)
    return () => document.removeEventListener('click', onClick, true)
  }, [])

  // Form fills — the point is to capture *abandoned* forms, so a field counts
  // as filled the moment it has a value, and anything still unreported is
  // flushed when the visitor leaves.
  useEffect(() => {
    // Fields already sent, keyed by path + field, so a visitor tabbing back
    // and forth doesn't spam the timeline.
    const reported = new Set<string>()
    // Fields typed into but not yet committed (still focused).
    const pending = new Map<string, FormField>()

    const report = (el: FormField) => {
      const value = el.value?.trim()
      const field = el.getAttribute('name') || el.id
      if (!value || !field) return

      const key = `${window.location.pathname}:${field}`
      pending.delete(key)
      if (reported.has(key)) return
      reported.add(key)

      trackEvent('form_field', {
        label: fieldLabel(el, field),
        meta: { field, value: previewValue(el, value) },
        stage: 'filled_details',
      })
    }

    const trackable = (target: EventTarget | null): FormField | null => {
      const el = target as FormField | null
      if (!el || !isFormField(el) || el.closest('[data-track-ignore]')) return null
      return el
    }

    // Typing marks the field pending; it's only reported once the visitor
    // moves on (or leaves the page).
    const onInput = (event: Event) => {
      const el = trackable(event.target)
      if (!el) return

      const field = el.getAttribute('name') || el.id
      if (field) pending.set(`${window.location.pathname}:${field}`, el)
    }

    // Selects and checkboxes commit immediately — no need to wait for blur.
    const onChange = (event: Event) => {
      const el = trackable(event.target)
      if (el && el.tagName !== 'INPUT') report(el)
    }

    const onFocusOut = (event: FocusEvent) => {
      const el = trackable(event.target)
      if (el) report(el)
    }

    /**
     * The last field a visitor touches never blurs — they just close the tab.
     * Without this flush the most interesting half of an abandoned checkout
     * would be missing.
     */
    const flushPending = () => {
      pending.forEach((el) => report(el))
      pending.clear()
    }

    const onVisibility = () => {
      if (document.visibilityState === 'hidden') flushPending()
    }

    document.addEventListener('input', onInput, true)
    document.addEventListener('change', onChange, true)
    document.addEventListener('focusout', onFocusOut, true)
    document.addEventListener('visibilitychange', onVisibility)
    window.addEventListener('pagehide', flushPending)
    const stopListening = router.on('before', flushPending)

    return () => {
      document.removeEventListener('input', onInput, true)
      document.removeEventListener('change', onChange, true)
      document.removeEventListener('focusout', onFocusOut, true)
      document.removeEventListener('visibilitychange', onVisibility)
      window.removeEventListener('pagehide', flushPending)
      stopListening()
    }
  }, [])

  return null
}

type FormField = HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement

function isFormField(el: Element): el is FormField {
  const tag = el.tagName
  if (tag === 'SELECT' || tag === 'TEXTAREA') return true
  if (tag !== 'INPUT') return false

  const type = (el as HTMLInputElement).type
  return !['submit', 'button', 'reset', 'hidden', 'file'].includes(type)
}

/** Prefer the field's visible <label>, falling back to its name attribute. */
function fieldLabel(el: HTMLElement, field: string): string {
  const id = el.id
  if (id) {
    const label = document.querySelector(`label[for="${CSS.escape(id)}"]`)
    // Strip the required marker so the timeline reads "Email", not "Email *".
    const text = label?.textContent?.replace(/[\s*:]+$/, '').trim()
    if (text) return text
  }
  return humanize(field)
}

/**
 * What we store for a filled field. Passwords are never recorded, and card-ish
 * fields are reduced to a length so nothing sensitive lands in the analytics
 * table. Everything else keeps its value: recovering an abandoned checkout is
 * the whole point of tracking unsubmitted forms.
 */
function previewValue(el: HTMLElement, value: string): string {
  const type = (el as HTMLInputElement).type
  const name = (el.getAttribute('name') || el.id || '').toLowerCase()

  if (type === 'password' || /card|cvc|cvv|secret|token/.test(name)) {
    return `${value.length} chars`
  }
  return value.slice(0, 120)
}

/**
 * JSX renders a valueless `data-track-cta` as the string "true", which is not
 * a label. Treat that (and an empty value) as "no label given".
 */
function namedValue(value?: string): string | undefined {
  const trimmed = value?.trim()
  if (!trimmed || trimmed === 'true') return undefined
  return trimmed
}

/**
 * A product card's whole text is price + tag + material + name mashed
 * together, so prefer the card's name element when there is one.
 */
function productLabel(el: HTMLElement): string | undefined {
  const card = el.closest('a') ?? el
  const name = card.querySelector('.sss-card__name')?.textContent?.trim()
  return name || elementLabel(el) || undefined
}

function elementLabel(el: HTMLElement): string {
  return (el.textContent || '').replace(/\s+/g, ' ').trim().slice(0, 80)
}

function humanize(value: string): string {
  return value
    .replace(/[_-]+/g, ' ')
    .replace(/\b\w/g, (char) => char.toUpperCase())
    .trim()
}
